import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import RecaptchaV2, { resetRecaptcha } from '../components/RecaptchaV2';
import { useAuthConfig } from '../hooks/useAuthConfig';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState(null);

  const { login, resendVerificationEmail, isAuthenticated, user } = useAuth();
  const { recaptchaSiteKey, recaptchaEnabled, loading: configLoading } = useAuthConfig();
  const navigate = useNavigate();
  const location = useLocation();

  const getPostLoginPath = (role) => {
    const redirectPath = location.state?.from;
    if (redirectPath && redirectPath.startsWith('/')) return redirectPath;

    switch (role) {
      case 'super_admin':
        return '/super-admin/dashboard';
      case 'manager':
        return '/manager/dashboard';
      case 'airlines_admin':
        return '/airlines-admin/dashboard';
      case 'airlines_manager':
        return '/airlines-manager/dashboard';
      default:
        return '/login';
    }
  };

  useEffect(() => {
    if (isAuthenticated && user) {
      navigate(getPostLoginPath(user.role), { replace: true });
    }
  }, [isAuthenticated, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNeedsVerification(false);
    setResendMessage('');

    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    if (recaptchaEnabled && !recaptchaToken) {
      setError('Please complete the reCAPTCHA challenge');
      return;
    }

    setLoading(true);
    const result = await login(email, password, recaptchaToken);
    setLoading(false);

    if (result.success) {
      navigate(getPostLoginPath(result.user.role), { replace: true });
    } else {
      setError(result.error);
      setNeedsVerification(!!result.needsVerification);
      setRecaptchaToken(null);
      resetRecaptcha();
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      setResendMessage('Enter your email address above first.');
      return;
    }

    setResendLoading(true);
    setResendMessage('');
    const result = await resendVerificationEmail(email);
    setResendLoading(false);
    setResendMessage(result.success ? result.message : result.error);
  };

  return (
    <div className="min-h-screen flex font-login bg-white">
      <div className="hidden lg:flex lg:w-1/2 bg-[#f9fafb] relative items-center justify-center px-16">
        <div className="max-w-md w-full">
          <img src="/vughylogo.svg" alt="Vughy" className="h-8 w-auto mb-12" />
          <h1 className="text-[30px] font-semibold text-ink leading-tight mb-3">
            Airline visa operations, simplified
          </h1>
          <p className="text-[16px] text-[#475467] leading-relaxed mb-10">
            Manage airline teams, track visa applications, and process routes — with the same Vughy
            experience you use across CRM.
          </p>
          <div className="rounded-menu border border-border bg-white p-5 shadow-input">
            <div className="flex items-center gap-1 text-[#FDB022] text-sm mb-3">★★★★★</div>
            <p className="text-sm text-ink-secondary leading-relaxed">
              “Vughy helps our airline team submit and track visa applications without switching tools.”
            </p>
            <div className="mt-4 flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary-soft text-primary flex items-center justify-center font-semibold">
                V
              </div>
              <div>
                <p className="text-sm font-semibold text-ink">Vughy for Airlines</p>
                <p className="text-xs text-ink-muted">Trusted travel operations platform</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-[400px]">
          <div className="mb-8">
            <img src="/vughylogo.svg" alt="Vughy" className="h-8 w-auto mb-6 lg:hidden" />
            <h2 className="text-[30px] font-semibold text-ink">Log in</h2>
            <p className="text-[16px] text-[#475467] mt-1">Welcome back. Please enter your details.</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-100 text-red-700 px-4 py-3 rounded-chip mb-5 text-sm">
              <div>{error}</div>
              {needsVerification && (
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={resendLoading}
                  className="mt-2 text-sm font-semibold text-primary hover:text-primary-hover disabled:opacity-50"
                >
                  {resendLoading ? 'Sending activation email...' : 'Resend activation email'}
                </button>
              )}
            </div>
          )}

          {resendMessage && (
            <div className="bg-primary-soft border border-primary/20 text-primary px-4 py-3 rounded-chip mb-5 text-sm">
              {resendMessage}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-ink-label mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3.5 py-2.5 border border-border-soft rounded-chip shadow-input focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-[16px] placeholder:text-ink-muted"
                placeholder="Enter your email"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-label mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-border-soft rounded-chip shadow-input focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-[16px] placeholder:text-ink-muted pr-16"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-medium text-ink-muted hover:text-primary"
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              <div className="mt-2 text-right">
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-primary hover:text-primary-hover"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            {!configLoading && (
              <RecaptchaV2 siteKey={recaptchaSiteKey} onChange={setRecaptchaToken} />
            )}

            <button
              type="submit"
              disabled={loading || configLoading}
              className="w-full bg-primary hover:bg-primary-hover text-white font-semibold py-2.5 px-[18px] rounded-chip shadow-input transition-all text-[16px] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-ink-muted">
            Don&apos;t have an account?{' '}
            <Link to="/signup" className="text-primary hover:text-primary-hover font-semibold">
              Register your airline
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
